import React from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { AddCalendarVacationDialog } from './components/add-calendar-vacation-dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { WarningDialog } from '../../../../../component/ui/warning-dialog/warning-dialog';
import { deleteCalendarVacationOperation } from './operations/delete-calendar-vacation-operation';
import { EditCalendarVacationDialog } from './components/edit-calendar-vacation-dialog';
import dateUtils from '../../../../../utils/date-format';

function formatDate(value: unknown): string {
  if (!value) return '-';
  const d = value instanceof Date ? value : new Date(value as any);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

export const CalendarVacation = observer(() => {
  const vacations = globalSettingsStores.calendarVacations || [];
  const [addDialogVisible, setAddDialogVisible] = React.useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = React.useState(false);
  const [selectedVacationIdToDelete, setSelectedVacationIdToDelete] =
    React.useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [editDialogVisible, setEditDialogVisible] = React.useState(false);
  const [selectedVacationToEdit, setSelectedVacationToEdit] = React.useState<
    any | null
  >(null);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <div style={{ color: '#4b5563' }}>
          כל חופשה בלוח החופשות תציג התראה למשתמשים במקרים בהם משובצות הרצאות
          בתאריכי חופשה.
        </div>
        <Button
          style={{ height: '30px', whiteSpace: 'nowrap' }}
          label="הוספת חופשה"
          onClick={() => setAddDialogVisible(true)}
        />
      </div>

      <Dialog
        header="הוספת חופשה"
        visible={addDialogVisible}
        style={{ width: '600px', direction: 'rtl' }}
        onHide={() => setAddDialogVisible(false)}
        modal
      >
        <AddCalendarVacationDialog
          closeDialog={() => setAddDialogVisible(false)}
        />
      </Dialog>

      <Dialog
        header="עריכת חופשה"
        visible={editDialogVisible}
        style={{ width: '600px', direction: 'rtl' }}
        onHide={() => {
          setEditDialogVisible(false);
          setSelectedVacationToEdit(null);
        }}
        modal
      >
        {selectedVacationToEdit && (
          <EditCalendarVacationDialog
            calendarVacationId={selectedVacationToEdit.id}
            initialTitle={selectedVacationToEdit.title ?? ''}
            initialDescription={selectedVacationToEdit.description ?? ''}
            initialStartDate={selectedVacationToEdit.startDate ?? null}
            initialEndDate={selectedVacationToEdit.endDate ?? null}
            closeDialog={() => {
              setEditDialogVisible(false);
              setSelectedVacationToEdit(null);
            }}
          />
        )}
      </Dialog>

      <WarningDialog
        header="מחיקת חופשה"
        isVisible={deleteDialogVisible}
        onHide={() => {
          if (!deleteLoading) {
            setDeleteDialogVisible(false);
            setSelectedVacationIdToDelete(null);
          }
        }}
        content="האם למחוק את החופשה?"
        dangerousContent="פעולה זו תמחק את החופשה לצמיתות."
        notInformation="שים לב: חופשה משפיעה על התראות במקרים בהם משובצות הרצאות בתאריכי חופשה."
        notInformationVariant="yellow"
        confirmLabel="מחיקה"
        confirmLoading={deleteLoading}
        onConfirm={async () => {
          if (selectedVacationIdToDelete == null) return;
          await deleteCalendarVacationOperation(
            selectedVacationIdToDelete,
            setDeleteLoading,
            () => {
              setDeleteDialogVisible(false);
              setSelectedVacationIdToDelete(null);
            },
          );
        }}
      />

      {vacations.length === 0 ? (
        <div>לא נמצאו חופשות</div>
      ) : (
        <DataTable
          value={vacations}
          dataKey="id"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          scrollable
          tableStyle={{ direction: 'rtl', marginTop: '8px' }}
        >
          <Column
            style={{ textAlign: 'right', height: '40px' }}
            header="כותרת"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: any) => <div>{rowData.title || '-'}</div>}
          />
          <Column
            style={{ textAlign: 'right', height: '40px' }}
            header="תיאור"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: any) => <div>{rowData.description || '-'}</div>}
          />
          <Column
            style={{ textAlign: 'right', height: '40px' }}
            header="מתאריך"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: any) => (
              <div>{dateUtils.displayFormatDate(rowData.startDate)}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right', height: '40px' }}
            header="עד תאריך"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: any) => (
              <div>{dateUtils.displayFormatDate(rowData.endDate)}</div>
            )}
          />

          <Column
            style={{ textAlign: 'right', height: '40px', width: '120px' }}
            header="פעולות"
            headerStyle={{
              height: '45px',
              maxHeight: '45px',
              padding: '0',
              paddingRight: '16.5px',
            }}
            body={(rowData: any) => {
              const isRowDeleting =
                deleteLoading && selectedVacationIdToDelete === rowData.id;

              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="button"
                    icon="pi pi-pencil"
                    className="p-button-text"
                    disabled={deleteLoading}
                    onClick={() => {
                      setSelectedVacationToEdit(rowData);
                      setEditDialogVisible(true);
                    }}
                    tooltip="עריכה"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Button
                    type="button"
                    icon={
                      isRowDeleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'
                    }
                    className="p-button-text p-button-danger"
                    disabled={deleteLoading}
                    onClick={() => {
                      setSelectedVacationIdToDelete(rowData.id);
                      setDeleteDialogVisible(true);
                    }}
                    tooltip="מחיקה"
                    tooltipOptions={{ position: 'top' }}
                  />
                </div>
              );
            }}
          />
        </DataTable>
      )}
    </div>
  );
});
